import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, ChevronsUpDown, Plus, Search, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import type { AxiosError } from "axios";

import { cn } from "@/marketing/lib/utils";
import { Button } from "@/marketing/components/ui/button";
import { Input } from "@/marketing/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/marketing/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/marketing/components/ui/popover";
import type {
  CommunityPostView,
  PickFeaturedItemInput,
} from "@/marketing/models/community";
import { communityAdminAPIClient } from "@/marketing/infrastructure/influencer/community/communityAdminAPIClient";
import { COMMUNITY_KEYS } from "@/marketing/infrastructure/influencer/community/utils/keys";
import { INFLUENCER_CAMPAIGN_QUERIES } from "@/marketing/infrastructure/influencer/campaign/utils/queries";
import { isBareUrl, isFeaturedContentUrl } from "@/marketing/lib/featuredContentUrl";

interface FeaturedDraft {
  contentUrl: string;
  comment: string;
}

interface PickFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  // When present, the dialog edits this pick; otherwise it creates a new one.
  pick?: CommunityPostView | undefined;
}

function toDrafts(pick?: CommunityPostView): FeaturedDraft[] {
  if (!pick || pick.featuredContents.length === 0) {
    return [{ contentUrl: "", comment: "" }];
  }
  return [...pick.featuredContents]
    .sort((a, b) => a.rank - b.rank)
    .map((item) => ({ contentUrl: item.contentUrl, comment: item.comment ?? "" }));
}

export function PickFormDialog({ open, onOpenChange, pick }: PickFormDialogProps) {
  const queryClient = useQueryClient();
  const isEdit = Boolean(pick);

  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [isPinned, setIsPinned] = useState(false);
  const [campaignId, setCampaignId] = useState("");
  const [items, setItems] = useState<FeaturedDraft[]>([{ contentUrl: "", comment: "" }]);

  useEffect(() => {
    if (!open) return;
    setTitle(pick?.title ?? "");
    setBody(pick?.body ?? "");
    setIsPinned(pick?.isPinned ?? false);
    setCampaignId(pick?.campaignId ?? pick?.campaign?.campaignId ?? "");
    setItems(toDrafts(pick));
  }, [open, pick]);

  const mutation = useMutation({
    mutationFn: async () => {
      const cleanedItems: PickFeaturedItemInput[] = items
        .filter((item) => item.contentUrl.trim() !== "")
        .map((item, index) => ({
          rank: index + 1,
          contentUrl: item.contentUrl.trim(),
          ...(item.comment.trim() !== "" && { comment: item.comment.trim() }),
        }));
      const payload = {
        ...(title.trim() !== "" && { title: title.trim() }),
        body: body.trim(),
        isPinned,
        ...(campaignId !== "" && { campaignId }),
        featuredContents: cleanedItems,
      };
      if (pick) {
        return communityAdminAPIClient.updatePick(pick.postId, payload);
      }
      return communityAdminAPIClient.createPick(payload);
    },
    onSuccess: () => {
      toast.success(isEdit ? "Pick updated" : "Pick published");
      queryClient.invalidateQueries({ queryKey: COMMUNITY_KEYS.picks() });
      onOpenChange(false);
    },
    onError: (error: AxiosError<{ message?: string }>) => {
      toast.error(error.response?.data?.message ?? "Failed to save pick");
    },
  });

  // Per-row validation: an entered contentUrl must be a TikTok video link (Instagram cannot
  // play on the creator-hub feed), and a comment must not be a bare URL.
  const itemError = (item: FeaturedDraft) => {
    const url = item.contentUrl.trim();
    const comment = item.comment.trim();
    return {
      contentUrl: url !== "" && !isFeaturedContentUrl(url) ? "Enter a TikTok video URL — Instagram is not supported (reels can't play on the creator hub)" : undefined,
      comment: comment !== "" && isBareUrl(comment) ? "Commentary only — don't paste a bare URL here" : undefined,
    };
  };

  const hasItem = items.some((item) => item.contentUrl.trim() !== "");
  const hasItemError = items.some((item) => {
    const error = itemError(item);
    return Boolean(error.contentUrl || error.comment);
  });
  const canSubmit = body.trim() !== "" && hasItem && !hasItemError && !mutation.isPending;

  const updateItem = (index: number, patch: Partial<FeaturedDraft>) => {
    setItems((prev) => prev.map((item, i) => (i === index ? { ...item, ...patch } : item)));
  };
  const addItem = () => setItems((prev) => [...prev, { contentUrl: "", comment: "" }]);
  const removeItem = (index: number) =>
    setItems((prev) => (prev.length === 1 ? prev : prev.filter((_, i) => i !== index)));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="tw-max-w-2xl">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit Skin Seoul Pick" : "New Skin Seoul Pick"}</DialogTitle>
        </DialogHeader>

        <div className="tw-flex tw-max-h-[60vh] tw-flex-col tw-gap-4 tw-overflow-y-auto tw-px-1">
          <div>
            <label className="tw-mb-1 tw-block tw-text-xs tw-font-semibold tw-text-gray-700">Title</label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="How top creators shoot the unboxing" />
          </div>

          <div>
            <label className="tw-mb-1 tw-block tw-text-xs tw-font-semibold tw-text-gray-700">
              Commentary <span className="tw-text-red-600">*</span>
            </label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Explain why these picks work — the best-practice lesson..."
              className="tw-min-h-[80px] tw-w-full tw-rounded-lg tw-border tw-border-gray-200 tw-px-3 tw-py-2 tw-text-sm tw-outline-none focus:tw-border-violet-500"
            />
          </div>

          <div>
            <label className="tw-mb-1 tw-block tw-text-xs tw-font-semibold tw-text-gray-700">
              Linked campaign
            </label>
            <CampaignCombobox value={campaignId} onChange={setCampaignId} />
          </div>

          <div>
            <div className="tw-mb-2 tw-flex tw-items-center tw-justify-between">
              <label className="tw-text-xs tw-font-semibold tw-text-gray-700">
                Featured content <span className="tw-text-red-600">*</span>
              </label>
              <span className="tw-text-xs tw-text-gray-400">rank = order, 1 = top</span>
            </div>
            <div className="tw-flex tw-flex-col tw-gap-2">
              {items.map((item, index) => {
                const error = itemError(item);
                return (
                <div key={index} className="tw-flex tw-items-start tw-gap-2 tw-rounded-lg tw-border tw-border-gray-200 tw-bg-gray-50 tw-p-2">
                  <span className="tw-mt-2 tw-grid tw-h-5 tw-w-5 tw-flex-none tw-place-items-center tw-rounded tw-bg-gray-900 tw-text-xs tw-font-bold tw-text-white">
                    {index + 1}
                  </span>
                  <div className="tw-flex-1 tw-space-y-1">
                    <Input
                      value={item.contentUrl}
                      onChange={(e) => updateItem(index, { contentUrl: e.target.value })}
                      placeholder="https://www.tiktok.com/@creator/video/123"
                      aria-invalid={Boolean(error.contentUrl)}
                    />
                    {error.contentUrl && <p className="tw-text-xs tw-text-red-600">{error.contentUrl}</p>}
                    <Input
                      value={item.comment}
                      onChange={(e) => updateItem(index, { comment: e.target.value })}
                      placeholder="Why this posting is good (optional)"
                      aria-invalid={Boolean(error.comment)}
                    />
                    {error.comment && <p className="tw-text-xs tw-text-red-600">{error.comment}</p>}
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="tw-mt-1 tw-flex-none tw-text-red-600"
                    onClick={() => removeItem(index)}
                    disabled={items.length === 1}
                  >
                    <Trash2 className="tw-h-4 tw-w-4" />
                  </Button>
                </div>
                );
              })}
            </div>
            <Button type="button" variant="outline" className="tw-mt-2 tw-w-full" onClick={addItem}>
              <Plus className="tw-mr-1 tw-h-4 tw-w-4" /> Add featured content
            </Button>
          </div>

          <label className="tw-flex tw-items-center tw-gap-2 tw-text-sm">
            <input type="checkbox" checked={isPinned} onChange={(e) => setIsPinned(e.target.checked)} />
            Pin to top of Picks
          </label>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={() => mutation.mutate()} disabled={!canSubmit}>
            {mutation.isPending ? "Saving..." : isEdit ? "Save changes" : "Publish Pick"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface CampaignComboboxProps {
  value: string;
  onChange: (campaignId: string) => void;
}

// Searchable, clearable campaign picker. Empty value = no campaign linked.
function CampaignCombobox({ value, onChange }: CampaignComboboxProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const { data: campaigns = [], isLoading } = useQuery(
    INFLUENCER_CAMPAIGN_QUERIES.list(),
  );

  const selected = campaigns.find((c) => c.campaignId === value);
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return campaigns;
    return campaigns.filter((c) => c.name.toLowerCase().includes(q));
  }, [campaigns, search]);

  const handleSelect = (campaignId: string) => {
    onChange(campaignId === value ? "" : campaignId);
    setOpen(false);
    setSearch("");
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setSearch("");
      }}
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="tw-w-full tw-justify-between tw-font-normal"
        >
          <span className={cn("tw-truncate", !selected && "tw-text-gray-400")}>
            {selected ? selected.name : "No campaign linked"}
          </span>
          <div className="tw-flex tw-flex-none tw-items-center tw-gap-1">
            {value && (
              <X
                className="tw-h-4 tw-w-4 tw-opacity-50 hover:tw-opacity-100"
                onClick={(e) => {
                  e.stopPropagation();
                  onChange("");
                }}
              />
            )}
            <ChevronsUpDown className="tw-h-4 tw-w-4 tw-opacity-50" />
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="tw-w-[--radix-popover-trigger-width] tw-p-0"
        align="start"
      >
        <div className="tw-flex tw-items-center tw-gap-2 tw-border-b tw-px-3">
          <Search className="tw-h-4 tw-w-4 tw-flex-none tw-opacity-50" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search campaigns..."
            className="tw-h-9 tw-w-full tw-bg-transparent tw-text-sm tw-outline-none placeholder:tw-text-gray-400"
          />
        </div>
        <div className="tw-max-h-60 tw-overflow-auto tw-p-1">
          {isLoading ? (
            <div className="tw-px-2 tw-py-3 tw-text-center tw-text-sm tw-text-gray-400">
              Loading...
            </div>
          ) : filtered.length === 0 ? (
            <div className="tw-px-2 tw-py-3 tw-text-center tw-text-sm tw-text-gray-400">
              No campaigns found
            </div>
          ) : (
            filtered.map((campaign) => (
              <button
                key={campaign.campaignId}
                type="button"
                onClick={() => handleSelect(campaign.campaignId)}
                className="tw-flex tw-w-full tw-items-center tw-gap-2 tw-rounded-sm tw-px-2 tw-py-1.5 tw-text-left tw-text-sm hover:tw-bg-accent"
              >
                <Check
                  className={cn(
                    "tw-h-4 tw-w-4 tw-flex-none",
                    campaign.campaignId === value ? "tw-opacity-100" : "tw-opacity-0",
                  )}
                />
                <span className="tw-truncate">{campaign.name}</span>
              </button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
